<template>
  <div class="subtitle-display" :class="{ 'is-demo': isDemo }" ref="subtitleContainer">
    <template v-for="(subtitle, index) in localSubtitles" :key="index">
    <SubtitleItem
      :data-subtitle-index="index"
      :formatted-start="formatTime(subtitle.start)"
      :display-text="getHighlightedText(subtitle, index)"
      :translated-text="translatedSubtitles && translatedSubtitles[index] ? translatedSubtitles[index].text : ''"
      :is-active="isActive(index)"
      :is-editing="editingIndex === index"
      :is-subtitles-visible="isSubtitlesVisible"
      :is-following-mode="isFollowingMode"
      :following-progress="followingProgress"
      :focus-distance="getFocusDistance(index)"
      :show-timestamps="showTimestamps"
      :show-edit-buttons="editable"
      :font-size="fontSize"
      :show-recording-actions="isRecordingMode"
      :has-recording="recordings.has(index)"
      :is-playing-recording="playingRecordingIndex === index"
      :assessment="getDisplayAssessment(index)"
      :is-assessment-open="assistantType === 'assessment' && assistantIndex === index"
      :is-explanation-open="assistantType === 'explanation' && assistantIndex === index"
      :editing-text="editingIndex === index ? editingText : ''"
      @play="playSubtitle(index)"
      @play-recording="$emit('play-recording', index)"
      @toggle-assessment-details="handleAssessmentDetailsToggle(index)"
      @toggle-explanation="toggleSentenceExplanation(index)"
      @word-click="handleWordClick($event, subtitle, index)"
      @start-edit="startEdit(index)"
      @finish-edit="finishEdit"
      @cancel-edit="cancelEdit"
      @update:editingText="updateEditingText"
    />
    </template>
    <WordLookupPopover
      :visible="wordLookup.visible"
      :word="wordLookup.word"
      :result="wordLookup.result"
      :loading="wordLookup.loading"
      :error="wordLookup.error"
      :saving="wordLookup.saving"
      :save-error="wordLookup.saveError"
      :position="wordLookup.position"
      @close="closeWordLookup"
      @save="saveCurrentWord"
      @retry="retryWordLookup"
    />
  </div>
</template>

<script>
import { formatTimeHHMMSS } from '@/utils/format'
import { apiPost } from '@/composables/useApi'
import { auth } from '@/firebase'
import SubtitleItem from './SubtitleItem.vue'
import WordLookupPopover from './WordLookupPopover.vue'
import { isVocabularySaved, loadSavedVocabulary, markVocabularySaved } from '@/composables/useSavedVocabulary'

const wordLookupCache = new Map()
const wordLookupCacheKey = (word, language, context) => (
  [auth.currentUser?.uid || '', language, word, context].join('\n')
)

export default {
  name: 'SubtitleDisplay',
  components: {
    SubtitleItem,
    WordLookupPopover,
  },
  emits: ['update-subtitles', 'play-subtitle', 'play-recording', 'open-learning-assistant'],
  props: {
    subtitles: {
      type: Array,
      default: () => [],
    },
    translatedSubtitles: {
      type: Array,
      default: null,
    },
    currentTime: {
      type: Number,
      default: 0,
    },
    playMode: {
      type: String,
      default: 'auto',
    },
    readAlongMode: {
      type: String,
      default: '',
    },
    audioId: {
      type: String,
      default: '',
    },
    audioName: { type: String, default: '' },
    materialId: { type: String, default: '' },
    editable: { type: Boolean, default: true },
    materialBook: { type: String, default: '' },
    activeSubtitleIndex: {
      type: Number,
      default: null,
    },
    words: {
      type: Array,
      default: () => [],
    },
    showTimestamps: {
      type: Boolean,
      default: true,
    },
    isSubtitlesVisible: {
      type: Boolean,
      default: true,
    },
    isWaitingForShadowing: {
      type: Boolean,
      default: false,
    },
    shadowingStartTime: {
      type: Number,
      default: 0,
    },
    shadowingDuration: {
      type: Number,
      default: 0,
    },
    isPlaying: {
      type: Boolean,
      default: false,
    },
    isDemo: {
      type: Boolean,
      default: false,
    },
    autoScrollFocusRatio: {
      type: Number,
      default: 0.5,
    },
    fontSize: {
      type: Number,
      default: 24,
    },
    isReadAlongMode: {
      type: Boolean,
      default: false,
    },
    recordings: {
      type: Object,
      default: () => new Map(),
    },
    playingRecordingIndex: {
      type: Number,
      default: null,
    },
    assessments: {
      type: Object,
      default: () => new Map(),
    },
    assistantType: {
      type: String,
      default: '',
    },
    assistantIndex: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      editingIndex: null,
      editingText: '',
      localSubtitles: [],
      subtitleWordsMap: new Map(),
      generatedWords: [],
      progressTimer: null,
      currentProgress: 0,
      isUserScrolling: false,
      userScrollTimeout: null,
      explanationStates: {},
      explanationQuota: null,
      wordLookup: {
        visible: false,
        word: '',
        loading: false,
        error: '',
        saving: false,
        saveError: '',
        result: null,
        position: null,
        anchorStart: null,
        context: '',
        subtitleIndex: null,
        requestId: 0,
      },
    }
  },
  computed: {
    isFollowingMode() {
      return this.readAlongMode === 'following'
    },
    isRecordingMode() {
      return this.isReadAlongMode && this.readAlongMode !== 'following'
    },
    followingProgress() {
      if (!this.isFollowingMode) return 0
      if (this.isWaitingForShadowing && this.currentSubtitle) {
        return this.currentProgress
      }
      return 0
    },
    currentSubtitle() {
      if (this.activeSubtitleIndex === null) return null
      return this.localSubtitles[this.activeSubtitleIndex]
    },
  },
  methods: {
    formatTime(seconds) {
      return formatTimeHHMMSS(seconds)
    },
    isActive(index) {
      if (!this.localSubtitles[index]) return false
      return index === this.activeSubtitleIndex
    },
    shouldAutoFollowSubtitle() {
      return this.isPlaying
    },
    getFocusedScrollTop(container, targetElement) {
      // 手机端底部有跟读栏和播放控制栏浮在字幕上，当前句放在偏上的位置
      const ratio = window.matchMedia('(max-width: 768px)').matches
        ? Math.min(this.autoScrollFocusRatio, 0.4)
        : this.autoScrollFocusRatio
      const subtitleFocusRatio = Math.min(1, Math.max(0, ratio))
      const containerHeight = container.clientHeight
      const targetCenter = this.getTargetCenter(container, targetElement)
      const targetScroll = targetCenter - (containerHeight * subtitleFocusRatio)
      const maxScroll = container.scrollHeight - containerHeight
      return Math.max(0, Math.min(targetScroll, maxScroll))
    },
    getTargetCenter(container, targetElement) {
      // 按实际屏幕位置换算：字幕容器不是定位元素，offsetTop 会从外层播放器算起，多出上方视频和工具栏的高度
      const containerRect = container.getBoundingClientRect()
      const targetRect = targetElement.getBoundingClientRect()
      return container.scrollTop + (targetRect.top - containerRect.top) + (targetRect.height / 2)
    },
    scrollElementIntoFocus(targetElement) {
      const container = this.$el
      if (!container || !targetElement) return

      container.scrollTo({
        top: this.getFocusedScrollTop(container, targetElement),
        behavior: 'smooth',
      })
    },
    scrollToActive() {
      if (this.isUserScrolling || !this.shouldAutoFollowSubtitle()) return

      const activeElement = this.$el.querySelector('.subtitle-line.active')
      if (!activeElement) return

      this.scrollElementIntoFocus(activeElement)
    },
    scrollToSubtitle(index) {
      if (index === null || index === undefined || index < 0) return

      this.$nextTick(() => {
        const container = this.$el
        const targetElement = container?.querySelectorAll?.('.subtitle-line')?.[index]
        if (!container || !targetElement) return

        this.scrollElementIntoFocus(targetElement)
      })
    },
    handleSubtitleChange() {
      this.$nextTick(() => {
        this.scrollToActive()
      })
    },
    handleActiveSubtitleChange(newIndex, oldIndex) {
      if (newIndex === null || newIndex === undefined || newIndex === -1) return
      if (newIndex === oldIndex) return
      if (!this.shouldAutoFollowSubtitle()) return

      this.handleSubtitleChange()
    },
    playSubtitle(index) {
      this.$emit('play-subtitle', index)
    },
    handleAssessmentDetailsToggle(index) {
      this.openAssessmentAssistant(index)
    },
    emitLearningAssistant(type, index) {
      const subtitle = this.localSubtitles[index]
      if (!subtitle) return
      this.$emit('open-learning-assistant', {
        type,
        index,
        subtitle,
        assessment: this.getDisplayAssessment(index),
        explanation: this.getExplanationState(index),
        explanationQuota: this.explanationQuota,
      })
    },
    openAssessmentAssistant(index) {
      const assessment = this.getDisplayAssessment(index)
      this.emitLearningAssistant('assessment', index)
    },
    async openSentenceExplanation(index) {
      const state = this.getExplanationState(index)
      this.emitLearningAssistant('explanation', index)
      // One request: the saved explanation comes back as is, otherwise one is generated.
      if (!state.loaded && !state.loading) await this.generateSentenceExplanation(index, { reuseExisting: true })
    },
    getExplanationState(index) {
      if (!this.explanationStates[index]) {
        this.explanationStates[index] = {
          entry: null,
          loading: false,
          loaded: false,
          error: '',
          retryable: false,
          followUpKind: '',
          followUpError: '',
          saving: false,
          saveError: '',
        }
      }
      return this.explanationStates[index]
    },
    refreshExplanationAssistant(index) {
      if (this.assistantType === 'explanation' && this.assistantIndex === index) {
        this.emitLearningAssistant('explanation', index)
      }
    },
    async toggleSentenceExplanation(index) {
      this.$nextTick(() => this.scrollToSubtitle(index))
      await this.openSentenceExplanation(index)
    },
    explanationTargetLanguage() {
      return this.$i18n?.locale === 'en' ? 'en' : 'zh-Hans'
    },
    explanationPayload(index) {
      return {
        sentence: this.localSubtitles[index].text,
        targetLanguage: this.explanationTargetLanguage(),
        contextBefore: this.localSubtitles[index - 1]?.text || '',
        contextAfter: this.localSubtitles[index + 1]?.text || '',
        translation: this.translatedSubtitles?.[index]?.text || '',
        audioId: this.audioId,
        audioName: this.audioName,
        materialId: this.materialId,
        materialBook: this.materialBook,
        subtitleIndex: index,
      }
    },
    setExplanationError(state, error, fallbackKey) {
      state.error = this.explanationErrorMessage(error, fallbackKey)
      state.retryable = error?.code !== 'monthly_limit_reached'
    },
    explanationErrorMessage(error, fallbackKey) {
      if (error?.code === 'monthly_limit_reached') return this.$t('sentenceExplanation.monthlyLimitReached')
      return error?.message || this.$t(fallbackKey)
    },
    async generateSentenceExplanation(index, { reuseExisting = false } = {}) {
      if (!this.localSubtitles[index]) return
      const state = this.getExplanationState(index)
      if (!auth.currentUser) {
        state.error = this.$t('sentenceExplanation.loginRequired')
        return
      }
      state.loading = true
      state.error = ''
      this.refreshExplanationAssistant(index)
      try {
        const result = await apiPost('/api/sentence-explanations/explain', {
          ...this.explanationPayload(index),
          reuseExisting,
        })
        state.entry = result.entry
        this.explanationQuota = result.quota
        state.loaded = true
      } catch (error) {
        this.setExplanationError(state, error, 'sentenceExplanation.generateFailed')
      } finally {
        state.loading = false
        this.refreshExplanationAssistant(index)
      }
    },
    async askExplanationFollowUp(index, kind) {
      if (!this.localSubtitles[index]) return
      const state = this.getExplanationState(index)
      state.followUpKind = kind
      state.followUpError = ''
      this.refreshExplanationAssistant(index)
      try {
        const result = await apiPost('/api/sentence-explanations/follow-up', {
          ...this.explanationPayload(index),
          kind,
        })
        state.entry = result.entry
        this.explanationQuota = result.quota
      } catch (error) {
        state.followUpError = this.explanationErrorMessage(error, 'sentenceExplanation.generateFailed')
      } finally {
        state.followUpKind = ''
        this.refreshExplanationAssistant(index)
      }
    },
    async toggleExplanationSaved(index) {
      const state = this.getExplanationState(index)
      if (!state.entry || state.saving) return
      state.saving = true
      state.saveError = ''
      this.refreshExplanationAssistant(index)
      try {
        const result = await apiPost('/api/sentence-explanations/save', {
          id: state.entry.id,
          saved: !state.entry.savedAt,
        })
        state.entry = result.entry
      } catch (error) {
        state.saveError = error?.message || this.$t('sentenceExplanation.saveFailed')
      } finally {
        state.saving = false
        this.refreshExplanationAssistant(index)
      }
    },
    closeWordLookup() {
      this.stopLookupAnchorTracking()
      this.wordLookup.visible = false
      this.wordLookup.loading = false
      this.wordLookup.error = ''
      this.wordLookup.saveError = ''
    },
    handleDocumentClick(event) {
      if (!this.wordLookup.visible) return
      if (event.target?.closest?.('.word-lookup-popover, .lookup-word')) return
      this.closeWordLookup()
    },
    handleWordClick(payload, subtitle, index) {
      const word = this.normalizeLookupWord(payload?.word)
      if (!word) return

      const requestId = this.wordLookup.requestId + 1
      this.wordLookup.visible = true
      this.wordLookup.word = word
      this.wordLookup.loading = true
      this.wordLookup.error = ''
      this.wordLookup.saveError = ''
      this.wordLookup.result = null
      this.wordLookup.position = payload?.position || null
      this.wordLookup.anchorStart = payload?.start ?? null
      this.wordLookup.context = subtitle?.text || ''
      this.wordLookup.subtitleIndex = index
      this.wordLookup.requestId = requestId
      this.startLookupAnchorTracking()

      if (!auth.currentUser) {
        this.wordLookup.loading = false
        this.wordLookup.error = this.$t('vocabulary.loginRequired')
        return
      }

      const cached = wordLookupCache.get(this.currentWordLookupCacheKey())
      if (cached) {
        this.wordLookup.result = { ...cached, saved: cached.saved && isVocabularySaved(word) }
        this.wordLookup.loading = false
        return
      }

      this.lookupWord({
        word,
        subtitle,
        subtitleIndex: index,
        requestId,
      })
    },
    findLookupAnchor() {
      const { subtitleIndex, anchorStart } = this.wordLookup
      if (anchorStart === null) return null
      const line = this.$el?.querySelectorAll?.('.subtitle-line')?.[subtitleIndex]
      return line?.querySelector(`.lookup-word[data-start="${anchorStart}"]`) || null
    },
    startLookupAnchorTracking() {
      this.stopLookupAnchorTracking()
      const track = () => {
        const anchor = this.findLookupAnchor()
        if (anchor) {
          const rect = anchor.getBoundingClientRect()
          const bounds = this.$el.getBoundingClientRect()
          if (rect.bottom < bounds.top || rect.top > bounds.bottom) {
            this.closeWordLookup()
            return
          }
          const position = this.wordLookup.position
          if (!position || position.top !== rect.top || position.left !== rect.left) {
            this.wordLookup.position = {
              left: rect.left,
              top: rect.top,
              right: rect.right,
              bottom: rect.bottom,
              width: rect.width,
              height: rect.height,
            }
          }
        }
        this.lookupAnchorFrame = requestAnimationFrame(track)
      }
      this.lookupAnchorFrame = requestAnimationFrame(track)
    },
    stopLookupAnchorTracking() {
      if (this.lookupAnchorFrame) cancelAnimationFrame(this.lookupAnchorFrame)
      this.lookupAnchorFrame = null
    },
    retryWordLookup() {
      const requestId = this.wordLookup.requestId + 1
      this.wordLookup.requestId = requestId
      this.wordLookup.loading = true
      this.wordLookup.error = ''
      this.lookupWord({
        word: this.wordLookup.word,
        subtitleIndex: this.wordLookup.subtitleIndex,
        requestId,
      })
    },
    currentWordLookupCacheKey() {
      return wordLookupCacheKey(this.wordLookup.word, this.explanationTargetLanguage(), this.wordLookup.context)
    },
    async lookupWord({ word, subtitle, subtitleIndex, requestId }) {
      const cacheKey = this.currentWordLookupCacheKey()
      try {
        const result = await apiPost('/api/vocabulary/lookup', {
          word,
          context: this.wordLookup.context,
          audioId: this.audioId,
          subtitleIndex,
          sourceLanguage: 'en',
          targetLanguage: this.explanationTargetLanguage(),
        })
        wordLookupCache.set(cacheKey, result)
        if (result.saved) markVocabularySaved(result.word)
        if (requestId !== this.wordLookup.requestId) return
        this.wordLookup.result = result
      } catch (error) {
        if (requestId !== this.wordLookup.requestId) return
        this.wordLookup.error = error?.message || this.$t('vocabulary.lookupFailed')
      } finally {
        if (requestId === this.wordLookup.requestId) {
          this.wordLookup.loading = false
        }
      }
    },
    async saveCurrentWord() {
      if (!this.wordLookup.result || this.wordLookup.result.saved || this.wordLookup.saving) return
      if (!auth.currentUser) {
        this.wordLookup.saveError = this.$t('vocabulary.loginRequired')
        return
      }

      this.wordLookup.saving = true
      this.wordLookup.saveError = ''
      try {
        const result = await apiPost('/api/vocabulary/save', {
          entry: this.wordLookup.result,
          context: this.wordLookup.context,
          audioId: this.audioId,
          audioName: this.audioName,
          materialId: this.materialId,
          materialBook: this.materialBook,
          subtitleIndex: this.wordLookup.subtitleIndex,
        })
        this.wordLookup.result = {
          ...this.wordLookup.result,
          ...result,
          saved: true,
        }
        const wordPrefix = wordLookupCacheKey(this.wordLookup.word, this.explanationTargetLanguage(), '')
        wordLookupCache.forEach((entry, key) => {
          if (key.startsWith(wordPrefix)) wordLookupCache.set(key, { ...entry, saved: true })
        })
        wordLookupCache.set(this.currentWordLookupCacheKey(), this.wordLookup.result)
        markVocabularySaved(this.wordLookup.result.word, this.wordLookup.result.lemma)
      } catch (error) {
        this.wordLookup.saveError = error?.message || this.$t('vocabulary.saveFailed')
      } finally {
        this.wordLookup.saving = false
      }
    },
    async startEdit(index) {
      if (!this.editable) return
      if (this.editingIndex !== null) {
        await this.finishEdit()
      }
      this.editingIndex = index
      this.editingText = this.localSubtitles[index].text
    },
    async finishEdit() {
      try {
        const newText = this.editingText.trim()
        if (!newText || !this.audioId) return

        const updatedSubtitles = [...this.localSubtitles]
        if (this.editingIndex >= 0 && this.editingIndex < updatedSubtitles.length) {
          updatedSubtitles[this.editingIndex] = {
            ...updatedSubtitles[this.editingIndex],
            text: newText,
          }
        }

        await apiPost(`/api/files/${this.audioId}/subtitles`, {
          subtitles: updatedSubtitles,
        })

        this.localSubtitles.splice(0, this.localSubtitles.length, ...updatedSubtitles)
        this.$emit('update-subtitles', updatedSubtitles)
        this.editingIndex = null
        this.editingText = ''
      } catch (error) {
        alert('保存失败: ' + (error.message || '未知错误'))
      }
    },
    cancelEdit() {
      this.editingIndex = null
    },
    updateEditingText(value) {
      this.editingText = value
    },
    handleKeyPress(event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return
      }

      if (event.code === 'Space') {
        event.preventDefault()
      }

      if (event.code === 'Enter' && this.playMode === 'reading' && this.activeSubtitleIndex !== null) {
        event.preventDefault()
        this.startEdit(this.activeSubtitleIndex)
      }
    },
    handleScroll() {
      this.isUserScrolling = true

      if (this.userScrollTimeout) {
        clearTimeout(this.userScrollTimeout)
      }

      this.userScrollTimeout = setTimeout(() => {
        this.isUserScrolling = false
        if (this.playMode !== 'reading' && this.shouldAutoFollowSubtitle()) {
          this.scrollToActive()
        }
      }, 3000)
    },
    updateSubtitleWordsMap() {
      this.subtitleWordsMap.clear()
      if (!this.subtitles.length || !this.generatedWords.length) return

      let wordCursor = 0

      this.subtitles.forEach((subtitle) => {
        const positions = []
        let lastPosition = -1
        let lastWordLength = 0
        let scanCursor = wordCursor

        while (scanCursor < this.generatedWords.length) {
          const word = this.generatedWords[scanCursor]

          if (word.end < subtitle.start) {
            scanCursor += 1
            wordCursor = scanCursor
            continue
          }

          if (word.start > subtitle.end) {
            break
          }

          const position = subtitle.text.indexOf(word.word, lastPosition + lastWordLength)
          if (position !== -1) {
            positions.push({
              word: word.word,
              position,
              start: word.start,
              end: word.end,
            })
            lastPosition = position
            lastWordLength = word.word.length
          }

          scanCursor += 1
        }

        if (positions.length) {
          this.subtitleWordsMap.set(subtitle.start, positions)
        }
      })
    },
    generateWords() {
      if (this.words && this.words.length) {
        this.generatedWords = [...this.words]
        this.updateSubtitleWordsMap()
        return
      }

      if (!this.subtitles || !this.subtitles.length) {
        this.generatedWords = []
        this.subtitleWordsMap.clear()
        return
      }

      const allWords = []
      this.subtitles.forEach((subtitle) => {
        const words = subtitle.text.match(/\b\w+\b|[\u4e00-\u9fa5]/g) || []
        const wordDuration = words.length ? (subtitle.end - subtitle.start) / words.length : 0

        words.forEach((word, index) => {
          allWords.push({
            word,
            start: subtitle.start + index * wordDuration,
            end: subtitle.start + (index + 1) * wordDuration,
          })
        })
      })

      this.generatedWords = allWords
      this.updateSubtitleWordsMap()
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    normalizeLookupWord(value) {
      const word = String(value || '')
        .trim()
        .replace(/^[^\p{L}\p{N}'-]+|[^\p{L}\p{N}'-]+$/gu, '')
        .toLowerCase()
      return /\p{L}/u.test(word) ? word : ''
    },
    getLookupRanges(text) {
      const ranges = []
      const matcher = /[\p{L}][\p{L}\p{M}'-]*/gu
      let match = matcher.exec(text)
      while (match) {
        const word = this.normalizeLookupWord(match[0])
        if (word) {
          ranges.push({
            start: match.index,
            end: match.index + match[0].length,
            word,
          })
        }
        match = matcher.exec(text)
      }
      return ranges
    },
    normalizeAssessmentWord(value) {
      return String(value || '').toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    },
    assessmentErrorTypeLabel(errorType) {
      if (!errorType || errorType === 'None') {
        return this.$t('audioPlayer.pronunciationAssessment.issueAccuracy')
      }
      return this.$t(`audioPlayer.pronunciationAssessment.errorTypes.${errorType}`)
    },
    findWordTiming(subtitle, wordInfo, cursor = 0) {
      const wordPositions = this.subtitleWordsMap.get(subtitle.start)
      if (!wordPositions?.length) return null

      const normalizedWord = this.normalizeAssessmentWord(wordInfo.word)
      if (!normalizedWord) return null

      return wordPositions.find((word) => {
        if (word.position < cursor) return false
        return this.normalizeAssessmentWord(word.word) === normalizedWord
      }) || null
    },
    getDisplayAssessment(index) {
      const assessment = this.assessments.get(index)
      const subtitle = this.localSubtitles[index]
      if (!assessment?.words?.length || !subtitle) return assessment

      let cursor = 0
      return {
        ...assessment,
        words: assessment.words.map((wordInfo) => {
          const timing = this.findWordTiming(subtitle, wordInfo, cursor)
          if (timing) {
            cursor = timing.position + timing.word.length
          }
          return {
            ...wordInfo,
            displayWord: timing?.word || wordInfo.word,
            start: timing?.start,
            end: timing?.end,
          }
        }),
      }
    },
    getAssessmentRanges(text, assessment) {
      if (!assessment?.words?.length) return []

      const ranges = []
      let cursor = 0

      assessment.words.forEach((wordInfo) => {
        const word = wordInfo.word || ''
        const normalizedWord = this.normalizeAssessmentWord(word)
        if (!normalizedWord) return

        const score = wordInfo.accuracyScore
        const hasIssue = wordInfo.errorType && wordInfo.errorType !== 'None'
        if (!hasIssue && (score === null || score === undefined || score >= 70)) return

        const candidates = Array.from(text.matchAll(/[\p{L}\p{N}'-]+/gu))
        const candidate = candidates.find((match) => {
          if (match.index < cursor) return false
          return this.normalizeAssessmentWord(match[0]) === normalizedWord
        })

        if (!candidate) return

        const issueClass = hasIssue
          ? `assessment-word-${wordInfo.errorType}`
          : score < 50
            ? 'assessment-word-poor'
            : 'assessment-word-needs-practice'

        ranges.push({
          start: candidate.index,
          end: candidate.index + candidate[0].length,
          className: issueClass,
          title: [
            `${this.$t('audioPlayer.pronunciationAssessment.accuracy')}: ${score ?? '-'}`,
            `${this.$t('audioPlayer.pronunciationAssessment.issue')}: ${this.assessmentErrorTypeLabel(wordInfo.errorType)}`,
          ].join('\n'),
        })
        cursor = candidate.index + candidate[0].length
      })

      return ranges
    },
    // 当前句按播放时间分成已读和未读：开始过的词算已读，句中停顿时未读部分保持淡色，
    // 正在念的词另加高亮。返回 { current, upcomingStart }，都按字符位置算
    getReadingProgress(subtitle, index) {
      if (index !== this.activeSubtitleIndex) return null
      const wordPositions = this.subtitleWordsMap.get(subtitle.start)
      if (!wordPositions) return null

      let lastStarted = -1
      while (lastStarted + 1 < wordPositions.length && wordPositions[lastStarted + 1].start <= this.currentTime) {
        lastStarted += 1
      }
      // 还没念到第一个词：播放中整句先淡着，暂停时不做区分
      if (lastStarted === -1) {
        return this.isPlaying ? { current: null, upcomingStart: wordPositions[0].position } : null
      }

      const word = wordPositions[lastStarted]
      const wordEnd = word.position + word.word.length
      return {
        current: this.currentTime <= word.end ? { start: word.position, end: wordEnd } : null,
        upcomingStart: wordEnd,
      }
    },
    // 播放时按与当前句的距离逐级淡化，最多算到第 4 行；暂停时全文保持可读
    getFocusDistance(index) {
      if (!this.isPlaying || this.activeSubtitleIndex === null) return 0
      return Math.min(Math.abs(index - this.activeSubtitleIndex), 4)
    },
    getHighlightedText(subtitle, index) {
      const text = subtitle.text || ''
      const assessmentRanges = this.getAssessmentRanges(text, this.assessments.get(index))
      const progress = this.getReadingProgress(subtitle, index)
      const lookupRanges = this.getLookupRanges(text)

      const ranges = [...assessmentRanges]
      if (progress?.current) {
        ranges.push({ ...progress.current, className: 'word-highlight', title: '' })
      }
      if (progress && progress.upcomingStart < text.length) {
        ranges.push({ start: progress.upcomingStart, end: text.length, className: 'word-upcoming', title: '' })
      }

      if (!ranges.length && !lookupRanges.length) {
        return this.escapeHtml(text)
      }

      const boundaries = new Set([0, text.length])
      ranges.forEach((range) => {
        boundaries.add(range.start)
        boundaries.add(range.end)
      })
      lookupRanges.forEach((range) => {
        boundaries.add(range.start)
        boundaries.add(range.end)
      })

      const points = Array.from(boundaries).sort((a, b) => a - b)
      return points.slice(0, -1).map((start, pointIndex) => {
        const end = points[pointIndex + 1]
        const chunk = text.slice(start, end)
        const activeRanges = ranges.filter((range) => start >= range.start && end <= range.end)
        const lookupRange = lookupRanges.find((range) => start === range.start && end === range.end)
        if (!activeRanges.length && !lookupRange) return this.escapeHtml(chunk)

        const classes = [
          ...activeRanges.map((range) => range.className),
          lookupRange ? 'lookup-word' : '',
          lookupRange && isVocabularySaved(lookupRange.word) ? 'saved-word' : '',
        ].filter(Boolean).join(' ')
        const title = activeRanges.map((range) => range.title).filter(Boolean).join('\n')
        const titleAttr = title ? ` title="${this.escapeHtml(title)}"` : ''
        const wordAttr = lookupRange
          ? ` data-word="${this.escapeHtml(lookupRange.word)}" data-start="${lookupRange.start}"`
          : ''
        return `<span class="${classes}"${wordAttr}${titleAttr}>${this.escapeHtml(chunk)}</span>`
      }).join('')
    },
  },
  watch: {
    subtitles: {
      immediate: true,
      handler(newSubtitles) {
        this.localSubtitles = [...newSubtitles]
        this.explanationStates = {}
        this.generateWords()
      },
    },
    activeSubtitleIndex: {
      handler(newIndex, oldIndex) {
        this.handleActiveSubtitleChange(newIndex, oldIndex)
      },
    },
    isPlaying: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.scrollToActive()
          })
        }
      },
    },
    isReadAlongMode(newValue) {
      if (!newValue) return
      this.isUserScrolling = false
      this.scrollToSubtitle(this.activeSubtitleIndex)
    },
    isWaitingForShadowing: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.currentProgress = 0
          this.progressTimer = setInterval(() => {
            if (this.currentSubtitle) {
              const duration = Math.max(this.shadowingDuration, 0.1)
              const elapsed = (Date.now() - this.shadowingStartTime) / 1000
              this.currentProgress = Math.min((elapsed / duration) * 100, 100)
              if (this.currentProgress >= 100) {
                clearInterval(this.progressTimer)
                this.progressTimer = null
              }
            }
          }, 50)
        } else {
          if (this.progressTimer) {
            clearInterval(this.progressTimer)
            this.progressTimer = null
          }
          this.currentProgress = 0
        }
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyPress)
    document.addEventListener('click', this.handleDocumentClick)
    this.$el.addEventListener('wheel', this.handleScroll, { passive: true })
    this.stopAuthListener = auth.onAuthStateChanged(() => loadSavedVocabulary())
    this.generateWords()
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress)
    document.removeEventListener('click', this.handleDocumentClick)
    this.$el.removeEventListener('wheel', this.handleScroll)
    this.stopAuthListener()
    this.stopLookupAnchorTracking()
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = null
    }
    if (this.userScrollTimeout) {
      clearTimeout(this.userScrollTimeout)
      this.userScrollTimeout = null
    }
  },
}
</script>

<style scoped>
.subtitle-display {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--space-1);
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: var(--space-4) var(--space-5) 5rem;
  box-sizing: border-box;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.subtitle-display > :deep(*) {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .subtitle-display {
    padding: var(--space-2) var(--space-2) 4rem;
  }
}
</style>
